/**
 * 강제 새로고침용 버전 확인 유틸리티
 */

// session -> 열려있는 탭에만 적용
import { useLanguage } from "assets/languages";
import { getSessionStorage, setSessionStorage } from "./sessionStorage";

interface VersionData {
  buildTime: string;
  version: string;
  buildId: string;
  impactedPathRegExps: string[];
}

let intervalId: number | null = null;
let intervalDuration = 3 * 60 * 1000;

/**
 * 주기적인 버전 확인 및 새로고침 시도 실행
 * @param intervalMs 기본값 3분
 */
export const initVersionChecker = (intervalMs = 3 * 60 * 1000) => {
  if (process.env.PRODUCTION_ENVIRONMENT !== 'true') {
    return;
  }
  intervalDuration = intervalMs;
  cancelVersionChecker();
  checkVersionAndReload();
  startInterval();
};

const startInterval = () => {
  cancelVersionChecker();
  intervalId = window.setInterval(() => {
    checkVersionAndReload();
  }, intervalDuration);
};

export const cancelVersionChecker = () => {
  if (intervalId == null) {
    return;
  }
  clearInterval(intervalId);
  intervalId = null;
}

const fetchVersionInfo = async (): Promise<VersionData | null> => {
  try {
    const response = await fetch(`/version.json?t=${new Date().getTime()}`);
    if (!response.ok) {
      return null;
    }
    return await response.json();
  } catch (error) {
    console.error('Failed to fetch version info:', error);
    return null;
  }
};

/**
 * 버전 확인 및 새로고침 실행
 * 필수 업데이트 경로가 아닌 경우에 미룰 수는 있다 (데이터 작성 중일 경우를 대비)
 */
const checkVersionAndReload = async () => {
  const language = useLanguage();
  if (process.env.PRODUCTION_ENVIRONMENT !== 'true') {
    return;
  }

  const storedVersionData = getSessionStorage('client_version');
  const newVersionData = await fetchVersionInfo();
  if (!newVersionData) {
    return;
  }
  
  if (!storedVersionData) {
    setSessionStorage('client_version', newVersionData);
    return;
  }
  
  if (newVersionData?.version === storedVersionData?.version) {
    return;
  }

  cancelVersionChecker();

  // 강제 새로고침이 필요한 페이지들
  if (isImpactedPath(newVersionData, window.location.pathname)) {
    window.alert(language.helper.versionChecker.important_update_detected_force);
    setSessionStorage('client_version', newVersionData);
    window.location.reload();
    return;
  }

  // 새로고침을 미룰 수 있는 페이지들
  if (window.confirm(language.helper.versionChecker.important_update_detected_confirm)) {
    setSessionStorage('client_version', newVersionData);
    window.location.reload();
    return;
  }

  // 새로고침을 미룰 경우
  window.alert(language.helper.versionChecker.important_update_detected_delayed);
  startInterval();
};

const isImpactedPath = (versionData: VersionData, path: string) => {
  if (!versionData?.impactedPathRegExps || versionData.impactedPathRegExps.length === 0) {
    return false;
  }
  return versionData.impactedPathRegExps.some((regex:string) => new RegExp(regex).test(path));
}
